/**
 * API client for `GET /api/v1/camps`.
 *
 * Implements WI-49: cross-school camps browse endpoint.
 *
 * Query parameters:
 *   sport      — sport slug (default "soccer_mens")
 *   from       — YYYY-MM-DD lower bound on start_date (default today)
 *   to         — YYYY-MM-DD upper bound on start_date (optional)
 *   division   — comma-separated division slugs (OR-unioned)
 *   region     — school region
 *   state      — comma-separated school state codes (OR-unioned)
 *   page       — 1-indexed (default 1)
 *   page_size  — 10–100 (default 25)
 */

export interface BrowseCampItem {
  camp_id: string;
  name: string;
  start_date: string;       // "YYYY-MM-DD"
  end_date: string | null;
  location_name: string | null;
  location_city: string | null;
  location_state: string | null;
  age_groups: string[];
  gender: 'mens' | 'womens' | 'coed';
  price_cents: number | null;
  registration_url: string | null;
  /**
   * URL of the page where this camp listing was found or is listed.
   * Promoted to public API in WI-50; is_archived remains internal.
   * Always present in the JSON response (value is null when not captured).
   */
  source_url: string | null;
  /**
   * True when the camp has no confirmed date. Hub-link rows (source = 'sidearm_camps_hub')
   * always have date_unknown = true. start_date will be the sentinel "9999-12-31" and must
   * never be displayed. WI-51b.
   */
  date_unknown: boolean;
  school_slug: string;
  school_name: string;
  division: string;
  region: string;
  school_state: string;
}

export interface CampsBrowseResponse {
  total: number;
  page: number;
  page_size: number;
  camps: BrowseCampItem[];
}

export interface CampsBrowseParams {
  sport?: string;
  from?: string;
  to?: string;
  division?: string[];
  region?: string;
  state?: string[];
  page?: number;
  page_size?: number;
  /**
   * When true, restrict results to the authenticated user's watchlisted schools.
   * Requires a valid session — the server returns 401 if unauthenticated.
   */
  watchlist?: boolean;
}

/**
 * Thrown when the server returns 401 — unauthenticated request with watchlist=true.
 * The component can catch this specifically to show AuthPromptSheet.
 */
export class CampsUnauthenticatedError extends Error {
  constructor() {
    super('Authentication required to use watchlist filter');
    this.name = 'CampsUnauthenticatedError';
  }
}

export async function fetchCampsBrowse(
  params: CampsBrowseParams = {},
  signal?: AbortSignal,
): Promise<CampsBrowseResponse> {
  const searchParams = new URLSearchParams();

  if (params.sport) searchParams.set('sport', params.sport);
  if (params.from) searchParams.set('from', params.from);
  if (params.to) searchParams.set('to', params.to);
  if (params.division && params.division.length > 0) {
    searchParams.set('division', params.division.join(','));
  }
  if (params.region) searchParams.set('region', params.region);
  if (params.state && params.state.length > 0) {
    searchParams.set('state', params.state.join(','));
  }
  if (params.page != null) searchParams.set('page', String(params.page));
  if (params.page_size != null) searchParams.set('page_size', String(params.page_size));
  if (params.watchlist) searchParams.set('watchlist', 'true');

  const qs = searchParams.toString();
  const url = `/api/v1/camps${qs ? `?${qs}` : ''}`;

  const response = await fetch(url, signal ? { signal } : undefined);
  if (response.status === 401) {
    throw new CampsUnauthenticatedError();
  }
  if (!response.ok) {
    throw new Error(`Camps fetch failed: ${response.status} ${response.statusText}`);
  }
  return response.json() as Promise<CampsBrowseResponse>;
}
